import Highcharts from 'highcharts';
import { ClickOnLegendType, ClickOnPointType } from '../../types';

export const getLineChartExportRasterImageProps = (type: string) => {
  return {
    exportOptions: {
      type,
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            style: {
              fontFamily: 'Arial',
            },
          },
        },
      },
      // 2400*900
      sourceWidth: 1200,
      sourceHeight: 450,
      scale: 2,
    },
    chartOptions: {
      chart: {
        style: {
          fontFamily: 'Arial',
        },
      },
      title: {
        style: {
          fontFamily: 'Arial',
          fontSize: '21px',
          fontWeight: 'bold',
        },
      },
    },
  };
};

export const getLineChartExportSVGProps = (type: string) => {
  return {
    exportOptions: {
      type,
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            style: {
              fontFamily: 'Arial',
            },
          },
        },
      },
      // 2400*900
      sourceWidth: 1200,
      sourceHeight: 450,
      scale: 2,
      style: {
        fontFamily: 'Arial',
      },
    },
    chartOptions: {
      chart: {
        style: {
          fontFamily: 'Arial',
        },
      },
      title: {
        style: {
          fontFamily: 'Arial',
          fontSize: '21px',
          fontWeight: 'bold',
        },
      },
      rangeSelector: {
        selected: 0,
        inputEnabled: false,
        allButtonsEnabled: false,
        buttons: [],
      },
    },
  };
};

export const getPieChartExportSVGProps = (type: string) => {
  return {
    exportOptions: {
      type,
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            style: {
              fontFamily: 'Arial',
            },
          },
        },
      },
      // 1200*900
      sourceWidth: 800,
      sourceHeight: 600,
      scale: 1.5,
      style: {
        fontFamily: 'Arial',
      },
    },
    chartOptions: {
      chart: {
        style: {
          fontFamily: 'Arial',
        },
      },
      title: {
        style: {
          fontFamily: 'Arial',
          fontSize: '21px',
          fontWeight: 'bold',
        },
      },
      rangeSelector: {
        selected: 0,
        inputEnabled: false,
        allButtonsEnabled: false,
        buttons: [],
      },
    },
  };
};

export const getPieChartExportRasterImageProps = (type: string) => {
  return {
    exportOptions: {
      type,
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
            style: {
              fontFamily: 'Arial',
            },
          },
        },
      },
      // 1200*900
      sourceWidth: 800,
      sourceHeight: 600,
      scale: 1.5,
    },
    chartOptions: {
      chart: {
        style: {
          fontFamily: 'Arial',
        },
      },
      title: {
        style: {
          fontFamily: 'Arial',
          fontSize: '21px',
          fontWeight: 'bold',
        },
      },
    },
  };
};

export const PRINT_SCALE = 0.32;

export const downloadPdf = (chartRef: any, width: number, height: number) => {
  const beforePrintOptions = {
    chart: {
      width: chartRef.chartWidth,
      height: chartRef.chartHeight,
    },
    title: {
      style: chartRef.options.title?.style,
    },
    plotOptions: {
      series: {
        dataLabels: {
          enabled: false,
        },
      },
      pie: {
        dataLabels: {
          enabled: false,
        },
      },
    },
  };

  chartRef.update(
    {
      chart: {
        width,
        height,
      },
      title: {
        style: {
          fontFamily: 'Arial',
          fontSize: '21px',
          fontWeight: 'bold',
        },
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: true,
          },
        },
        pie: {
          dataLabels: {
            enabled: true,
            formatter: function (this: Highcharts.PointLabelObject) {
              const originalY = (this.point as any).originalY;
              const displayValue = originalY !== undefined ? originalY : this.point.y;
              return `${this.point.name}: <b>${displayValue}</b>`;
            },
          },
        },
      },
    },
    false,
    false,
    false,
  );

  chartRef.exportChartLocal(
    { type: 'application/pdf' },
    {
      chart: {
        style: {
          fontFamily: 'Arial',
        },
      },
      title: {
        style: {
          fontFamily: 'Arial',
          fontSize: '21px',
          fontWeight: 'bold',
        },
      },
    },
  );

  chartRef.update(beforePrintOptions, false, false, false);
};

export abstract class PrintableChart {
  abstract chartRef?: any;
  onPrint?: () => void;
  beforePrintOptions?: any;
  printWidth: number;
  printHeight: number;
  printScale: number;

  constructor(printWidth: number, printHeight: number, printScale: number) {
    this.printWidth = printWidth;
    this.printHeight = printHeight;
    this.printScale = printScale;
  }

  public onBeforePrint = async (): Promise<any> => {
    if (!this.chartRef) {
      return;
    }

    this.beforePrintOptions = {
      chart: {
        width: this.chartRef.chartWidth,
        height: this.chartRef.chartHeight,
      },
      plotOptions: {
        series: {
          dataLabels: {
            enabled: false,
          },
        },
        pie: {
          dataLabels: {
            enabled: false,
          },
        },
      },
    };

    this.chartRef.update(
      {
        chart: {
          width: this.printWidth,
          height: this.printHeight,
        },
        plotOptions: {
          series: {
            dataLabels: {
              enabled: true,
            },
          },
          pie: {
            dataLabels: {
              enabled: true,
              formatter: function (this: Highcharts.PointLabelObject) {
                const originalY = (this.point as any).originalY;
                const displayValue = originalY !== undefined ? originalY : this.point.y;
                return `${this.point.name}: <b>${displayValue}</b>`;
              },
            },
          },
        },
      },
      false,
      false,
      false,
    );

    this.chartRef.container.style.transform = `scale(${
      this.printScale * PRINT_SCALE
    })`;
    this.chartRef.container.style.transformOrigin = 'left top';
  };

  public onAfterPrint = () => {
    if (!this.chartRef) {
      return;
    }
    this.chartRef.update(this.beforePrintOptions, false, false, false);
    this.beforePrintOptions = undefined;
    this.chartRef.container.style.transform = '';
    this.chartRef.container.style.transformOrigin = undefined;
  };
}

export function setSeriesOpacity(clickedSeries: Highcharts.Series) {
  let chart = clickedSeries.chart;
  chart.series.forEach(function (series) {
    if (series !== clickedSeries) {
      series.update(
        {
          opacity: 0.1,
          zIndex: 1,
        } as any,
        false,
      );
    } else {
      series.update(
        {
          opacity: 1,
          zIndex: 999,
        } as any,
        false,
      );
    }
  });
  chart.redraw();
}

export function legendItemClick(
  data: Highcharts.SeriesLegendItemClickEventObject,
  clickOnLegend: ClickOnLegendType | undefined,
) {
  data.preventDefault();
  const chartTitle = data.target.chart.options.title?.text ?? 'None';
  if (!!clickOnLegend && data.target.options.id) {
    clickOnLegend(data.target.options.id, data.target.name, chartTitle, data.target.userOptions?.custom?.total);
  }
}

export function pointItemClick(
  data: Highcharts.PointClickEventObject,
  clickOnPoint: ClickOnPointType | undefined,
) {
  const { point } = data as Highcharts.PointClickEventObject & {
    point: Highcharts.Point & { id: string };
  };
  const chartTitle = data.point.series.chart.options.title?.text ?? 'None';

  if (!!clickOnPoint && point.series.options.id && point.x) {
    const isLastPoint = point.series.points[point.series.points.length - 1].x === point.x;
    const element = point.graphic?.element;
    clickOnPoint(
      point.series.options.id,
      point.series.name,
      chartTitle,
      point.id,
      point.y,
      Highcharts.dateFormat('%m/%d/%y', Number(point.x)),
      Number(point.x), // raw timestamp for V2 API
      isLastPoint,
      element
    );
  }
}

/**
 * Pie chart data point with original value preserved
 */
export interface PieDataPoint {
  id?: string;
  name: string;
  y: number;
  originalY?: number;
  color?: string;
  [key: string]: any;
}

/**
 * Transform pie chart data to ensure minimum segment angle
 * Small segments are enlarged to be visible, original values preserved for tooltips
 * @param data - Array of pie chart data points
 * @param minAngle - Minimum angle in degrees (default 8°)
 * @returns Transformed data with originalY preserved
 */
export function applyMinSliceAngle<T extends { y: number }>(
  data: T[],
  minAngle: number = 5
): (T & { originalY: number })[] {
  if (!data || data.length === 0) return [];

  const total = data.reduce((sum, item) => sum + (item.y || 0), 0);
  if (total === 0) return data.map(item => ({ ...item, originalY: item.y }));

  // Calculate minimum value for the given angle
  // angle / 360 = value / total => value = total * angle / 360
  const minValue = (total * minAngle) / 360;

  return data.map(item => ({
    ...item,
    originalY: item.y,
    y: Math.max(item.y || 0, minValue),
  }));
}

/**
 * Format pie chart data label with word wrapping
 * Uses originalY if available (for min slice angle feature)
 * @param name - Point name
 * @param value - Point value (may be adjusted for visibility)
 * @param originalValue - Original value before adjustment (optional)
 * @param maxChars - Max characters per line (default 12 ~ 80px)
 * @returns Formatted HTML string with line breaks
 */
export function formatPieDataLabel(
  name: string,
  value: number | undefined,
  originalValue?: number,
  maxChars: number = 12
): string {
  let formattedName = name;

  if (name.length > maxChars) {
    const words = name.split(/(\s+|&)/);
    const lines: string[] = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + word).length <= maxChars) {
        currentLine += word;
      } else {
        if (currentLine) lines.push(currentLine.trim());
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine.trim());

    formattedName = lines.join('<br/>');
  }

  // Use original value if available (for min slice angle feature)
  const displayValue = originalValue !== undefined ? originalValue : value;

  return `${formattedName}:<br/><b>${displayValue}</b>`;
}
